import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { apiGet, apiRequest } from '../../utils/fetch';
import { GenericResponseInterface } from '../../interfaces/interfaces';

interface PayoutResult {
  userId?: number;
  payoutId?: number;
  amount?: string;
  itemsCount?: number;
  success: boolean;
  error?: string;
  transferId?: string;
}

interface ProcessResults {
  processed: number;
  succeeded: number;
  failed: number;
  totalAmount?: number;
  payouts: PayoutResult[];
}

const PayoutsManagementPage: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createResults, setCreateResults] = useState<ProcessResults | null>(null);
  const [processResults, setProcessResults] = useState<ProcessResults | null>(null);

  const handleGetUserById = async () => {
    setError(null);
    setSuccess(null);
    setUser(null);
    setCreateResults(null);
    setProcessResults(null);

    if (!userId) {
      return setError('Please enter a user ID');
    }

    setLoading(true);
    try {
      const result = await apiGet<GenericResponseInterface>(`/users/getUserData/userId/${userId}`);
      if (result.status && result.data) {
        setUser(result.data);
        setSuccess(`User found: ${result.data.name || 'N/A'} (${result.data.email || 'N/A'})`);
      } else {
        setError(result.message || 'Failed to fetch user');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEligiblePayoutsForUser = async () => {
    if (!userId) {
      return setError('Please enter a user ID');
    }

    setError(null);
    setSuccess(null);
    setCreateResults(null);
    setLoading(true);

    try {
      const result = await apiRequest<GenericResponseInterface>(
        `/payout/create-eligible/${userId}`,
        'POST'
      );

      if (result.status && result.data) {
        setSuccess(result.message || 'Payout created successfully');
        setCreateResults({
          processed: 1,
          succeeded: result.data.success ? 1 : 0,
          failed: result.data.success ? 0 : 1,
          payouts: [{
            userId: parseInt(userId),
            payoutId: result.data.payoutId,
            amount: result.data.amount,
            itemsCount: result.data.itemsCount,
            success: result.data.success
          }]
        });
      } else {
        setError(result.message || 'Failed to create payout');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create payout');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessReadyPayoutsForUser = async () => {
    if (!userId) {
      return setError('Please enter a user ID');
    }

    setError(null);
    setSuccess(null);
    setProcessResults(null);
    setLoading(true);

    try {
      const result = await apiRequest<GenericResponseInterface>(
        `/payout/process/${userId}`,
        'POST'
      );

      if (result.status && result.data) {
        setSuccess(result.message || 'Payouts processed successfully');
        setProcessResults(result.data);
      } else {
        setError(result.message || 'Failed to process payouts');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process payouts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEligiblePayoutsForAll = async () => {
    setError(null);
    setSuccess(null);
    setCreateResults(null);
    setLoading(true);

    try {
      const result = await apiRequest<GenericResponseInterface>(
        '/payout/create-eligible',
        'POST'
      );

      if (result.status && result.data) {
        setSuccess(result.message || 'Payouts created successfully');
        setCreateResults(result.data);
      } else {
        setError(result.message || 'Failed to create payouts');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create payouts');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessAllReadyPayouts = async () => {
    setError(null);
    setSuccess(null);
    setProcessResults(null);
    setLoading(true);

    try {
      const result = await apiRequest<GenericResponseInterface>(
        '/payout/process',
        'POST'
      );

      if (result.status && result.data) {
        setSuccess(result.message || 'Payouts processed successfully');
        setProcessResults(result.data);
      } else {
        setError(result.message || 'Failed to process payouts');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process payouts');
    } finally {
      setLoading(false);
    }
  };

  const renderResultsTable = (results: ProcessResults, type: 'create' | 'process') => {
    if (!results || results.payouts.length === 0) return null;

    return (
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {type === 'create' ? 'Created Payouts' : 'Processed Payouts'}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Chip label={`Total: ${results.processed}`} sx={{ mr: 1 }} />
          <Chip label={`Succeeded: ${results.succeeded}`} color="success" sx={{ mr: 1 }} />
          <Chip label={`Failed: ${results.failed}`} color="error" />
          {results.totalAmount !== undefined && (
            <Chip 
              label={`Total Amount: $${(results.totalAmount / 100).toFixed(2)}`} 
              color="primary" 
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User ID</TableCell>
                {type === 'create' && <TableCell>Payout ID</TableCell>}
                <TableCell>Amount</TableCell>
                <TableCell>Items</TableCell>
                {type === 'process' && <TableCell>Transfer ID</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.payouts.map((payout, index) => (
                <TableRow key={index}>
                  <TableCell>{payout.userId || 'N/A'}</TableCell>
                  {type === 'create' && <TableCell>{payout.payoutId || '-'}</TableCell>}
                  <TableCell>${payout.amount || '0.00'}</TableCell>
                  <TableCell>{payout.itemsCount || 0}</TableCell>
                  {type === 'process' && <TableCell>{payout.transferId || '-'}</TableCell>}
                  <TableCell>
                    <Chip
                      label={payout.success ? 'Success' : 'Failed'}
                      color={payout.success ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{payout.error || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Payouts Management
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Section 1: User-Specific Operations */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          User-Specific Operations
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            label="User ID"
            value={userId}
            onChange={e => setUserId(e.target.value)}
            size="small"
            type="number"
            sx={{ width: 150 }}
          />
          <Button 
            variant="outlined" 
            onClick={handleGetUserById}
            disabled={loading || !userId}
          >
            Get User
          </Button>
        </Box>

        {user && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>User:</strong> {user.name} ({user.email})
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreateEligiblePayoutsForUser}
            disabled={loading || !userId}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Create Eligible Payouts
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleProcessReadyPayoutsForUser}
            disabled={loading || !userId}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Process Ready Payouts
          </Button>
        </Box>

        {createResults && renderResultsTable(createResults, 'create')}
        {processResults && renderResultsTable(processResults, 'process')}
      </Paper>

      {/* Section 2: Bulk Operations */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Bulk Operations (All Users)
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Warning:</strong> These operations will affect ALL users in the system.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreateEligiblePayoutsForAll}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Create Eligible Payouts for All
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleProcessAllReadyPayouts}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Process All Ready Payouts
          </Button>
        </Box>

        {createResults && renderResultsTable(createResults, 'create')}
        {processResults && renderResultsTable(processResults, 'process')}
      </Paper>
    </Box>
  );
};

export default PayoutsManagementPage;
